export type Primitive =
    | "string"
    | "number"
    | "boolean"
    | "date"
    | "json"
    | "string?"
    | "number?"
    | "boolean?"
    | "date?"
    | "json?"

export const GRAPH = {
    event_logs: {
        table: "event_logs",
        alias: "el",
        scopes: ["org", "project"],
        keys: ["id"],
        schema: {
            id: "string",
            kind: "string",
            org_id: "string",
            project_id: "string?",
            created_by: "string",
            created_at: "date",
            data: "json",
        },
        computed: {
            subject_type: {
                expr: "COALESCE(${alias}.data->>'layerType', ${alias}.data->>'entityType')",
                type: "string?",
            },
        },
        edges: {},
    },
    missions: {
        table: "missions",
        alias: "m",
        scopes: ["org"],
        keys: ["id"],
        softDelete: "deleted_at",
        schema: {
            id: "string",
            pilot_id: "string?",
            aircraft_id: "string?",
            org_id: "string",
            group_id: "string?",
            provider: "string",
            external_id: "string",
            user_id: "string?",
        },
        edges: {
            pilot: { resource: "pilots", on: "m.pilot_id = p.id" },
            aircraft: { resource: "aircraft", on: "m.aircraft_id = a.id" },
            metadata: { resource: "metadata", on: "m.id = md.mission_id" },
            logs: { resource: "logs", on: "m.id = ml.mission_id" },
            group: { resource: "mission_groups", on: "m.group_id = mg.id" },
            checklist_links: { resource: "checklist_record_missions", on: "m.id = crm.mission_id" },
            tags_link: { resource: "mission_tags", on: "m.id = mt.mission_id" },
            certificates: { resource: "user_certificates", on: "m.user_id = pc.user_id" },
            linked_layers: { resource: "mission_linked_layers", on: "m.id = mll.mission_id" },
            files: { resource: "mission_files", on: "m.id = mif.mission_id" },
            custom_fields: { resource: "mission_custom_fields", on: "m.id = mcf.mission_id" },
        },
    },
    metadata: {
        table: "mission_metadata",
        alias: "md",
        scopes: ["org"],
        keys: ["mission_id"],
        schema: {
            mission_id: "string",
            org_id: "string",
            max_speed: "number?",
            avg_speed: "number?",
            max_altitude: "number?",
            avg_altitude: "number?",
            duration: "number?",
            distance_flown: "number?",
            airtime: "number?",
            started_at: "date",
            notes: "string?",
            location_name: "string?",
            max_battery_temp: "number?",
            avg_battery_temp: "number?",
            avg_signal_quality: "number?",
            min_battery_percent: "number?",
            max_battery_percent: "number?",
            avg_battery_percent: "number?",
            takeoff_latitude: "number?",
            takeoff_longitude: "number?",
        },
        edges: {
            mission: { resource: "missions", on: "md.mission_id = m.id", joinType: "inner" },
            tags_link: { resource: "mission_tags", on: "md.mission_id = mt.mission_id" },
        },
        computed: {
            airtime_minutes: "(${alias}.airtime::FLOAT / 60)",
            airtime_hours: "(${alias}.airtime::FLOAT / 3600)",
            duration_minutes: "(${alias}.duration::FLOAT / 60)",
            duration_hours: "(${alias}.duration::FLOAT / 3600)",
        },
    },
    logs: {
        table: "mission_logs",
        alias: "ml",
        scopes: ["org"],
        keys: ["id"],
        schema: {
            id: "string",
            mission_id: "string",
            org_id: "string",
            timestamp: "date?",
            latitude: "number?",
            longitude: "number?",
            battery_percent: "number?",
            height: "number?",
            altitude: "number?",
            speed_horizontal: "number?",
            speed_vertical: "number?",
            battery_temp: "number?",
            signal_quality: "number?",
            heading: "number?",
        },
        edges: {
            mission: { resource: "missions", on: "ml.mission_id = m.id", joinType: "inner" },
        },
    },
    pilots: {
        table: "pilots",
        alias: "p",
        scopes: ["org"],
        keys: ["id"],
        schema: {
            id: "string",
            org_id: "string",
            name: "string?",
            email: "string?",
            provider: "string",
            external_id: "string",
        },
        edges: {
            missions: { resource: "missions", on: "p.id = m.pilot_id" },
        },
    },
    user_certificates: {
        table: "user_certificates",
        alias: "pc",
        scopes: ["org"],
        keys: ["id"],
        schema: {
            id: "string",
            org_id: "string",
            user_id: "string",
            name: "string",
            certificate_number: "string?",
            issued_at: "date?",
            expires_at: "date?",
            expiry_config: "json?",
            notes: "string?",
            file_name: "string?",
            file_size_bytes: "number?",
            created_at: "date",
            updated_at: "date?",
        },
        edges: {
            missions: { resource: "missions", on: "pc.user_id = m.user_id" },
        },
    },
    aircraft: {
        table: "aircraft",
        alias: "a",
        scopes: ["org"],
        keys: ["id"],
        softDelete: "deleted_at",
        schema: {
            id: "string",
            org_id: "string",
            nickname: "string?",
            manufacturer: "string",
            model: "string?",
            serial_number: "string",
            registration_number: "string?",
            purchased_at: "date?",
            warranty_expires_at: "date?",
            notes: "string?",
            manual_status: "string",
            status_updated_at: "date?",
            status_updated_by: "string?",
            deleted_at: "date?",
            deleted_by: "string?",
            updated_at: "date?",
            updated_by: "string?",
        },
        edges: {
            missions: { resource: "missions", on: "a.id = m.aircraft_id" },
            maintenance_records: { resource: "maintenance_records", on: "a.id = mr.aircraft_id" },
            maintenance_template_links: {
                resource: "maintenance_template_aircraft",
                on: "a.id = mta.aircraft_id",
            },
        },
    },
    maintenance_templates: {
        table: "maintenance_template",
        alias: "mtp",
        scopes: ["org"],
        keys: ["id"],
        softDelete: "deleted_at",
        schema: {
            id: "string",
            org_id: "string",
            name: "string",
            description: "string?",
            instructions: "string?",
            condition_usage_flights: "number?",
            condition_duration_hours: "number?",
            condition_interval_value: "number?",
            status_on_trigger: "string",
            apply_to_all: "boolean",
            model_targets: "json",
            deleted_at: "date?",
            deleted_by: "string?",
            created_at: "date",
            created_by: "string",
            updated_at: "date?",
            updated_by: "string?",
        },
        edges: {
            records: { resource: "maintenance_records", on: "mtp.id = mr.template_id" },
            aircraft_links: {
                resource: "maintenance_template_aircraft",
                on: "mtp.id = mta.template_id",
            },
        },
    },
    maintenance_template_aircraft: {
        table: "maintenance_template_aircraft",
        alias: "mta",
        scopes: ["org"],
        keys: ["template_id", "aircraft_id"],
        schema: {
            template_id: "string",
            aircraft_id: "string",
            org_id: "string",
        },
        edges: {
            template: {
                resource: "maintenance_templates",
                on: "mta.template_id = mtp.id",
                joinType: "inner",
            },
            aircraft: { resource: "aircraft", on: "mta.aircraft_id = a.id", joinType: "inner" },
        },
    },
    currency_policies: {
        table: "currency_policies",
        alias: "cp",
        scopes: ["org"],
        keys: ["id"],
        schema: {
            id: "string",
            org_id: "string",
            name: "string",
            metric: "string",
            threshold: "number",
            window_days: "number",
            warning_days: "number",
            drone_types: "json",
            email_reminder: "boolean",
            notify_all_users: "boolean",
            created_at: "date",
            created_by: "string",
            updated_at: "date?",
            updated_by: "string?",
        },
        edges: {
            user_links: { resource: "currency_policy_users", on: "cp.id = cpu.policy_id" },
            recipient_links: {
                resource: "currency_policy_recipients",
                on: "cp.id = cpr.policy_id",
            },
        },
    },
    currency_policy_users: {
        table: "currency_policy_users",
        alias: "cpu",
        scopes: ["org"],
        keys: ["policy_id", "user_id"],
        schema: {
            policy_id: "string",
            user_id: "string",
            org_id: "string",
            assigned_at: "date",
        },
        edges: {
            policy: {
                resource: "currency_policies",
                on: "cpu.policy_id = cp.id",
                joinType: "inner",
            },
        },
    },
    currency_policy_recipients: {
        table: "currency_policy_recipients",
        alias: "cpr",
        scopes: ["org"],
        keys: ["id"],
        schema: {
            id: "string",
            policy_id: "string",
            org_id: "string",
            user_id: "string?",
            email: "string?",
        },
        edges: {
            policy: {
                resource: "currency_policies",
                on: "cpr.policy_id = cp.id",
                joinType: "inner",
            },
        },
    },
    maintenance_records: {
        table: "maintenance_record",
        alias: "mr",
        scopes: ["org"],
        keys: ["id"],
        softDelete: "deleted_at",
        schema: {
            id: "string",
            template_id: "string",
            aircraft_id: "string",
            org_id: "string",
            performed_by: "string?",
            date_performed: "date",
            notes: "string?",
            skipped: "boolean",
            deleted_at: "date?",
            deleted_by: "string?",
            created_at: "date",
            created_by: "string",
        },
        computed: {
            flights_since_last_service: `(
                WITH last_service AS (
                    SELECT date_performed FROM maintenance_record 
                    WHERE aircraft_id = \${alias}.aircraft_id 
                      AND template_id = \${alias}.template_id 
                      AND date_performed < \${alias}.date_performed 
                      AND deleted_at IS NULL 
                    ORDER BY date_performed DESC LIMIT 1
                )
                SELECT COUNT(m.id) 
                FROM missions m 
                INNER JOIN mission_metadata md ON m.id = md.mission_id 
                WHERE m.aircraft_id = \${alias}.aircraft_id 
                  AND m.deleted_at IS NULL
                  AND md.started_at <= \${alias}.date_performed
                  AND (
                      md.started_at > (SELECT date_performed FROM last_service)
                      OR NOT EXISTS (SELECT 1 FROM last_service)
                  )
            )`,

            hours_since_last_service: `(
                WITH last_service AS (
                    SELECT date_performed FROM maintenance_record 
                    WHERE aircraft_id = \${alias}.aircraft_id 
                      AND template_id = \${alias}.template_id 
                      AND date_performed < \${alias}.date_performed 
                      AND deleted_at IS NULL 
                    ORDER BY date_performed DESC LIMIT 1
                )
                SELECT COALESCE(SUM(md.airtime::FLOAT / 3600), 0) 
                FROM missions m 
                INNER JOIN mission_metadata md ON m.id = md.mission_id 
                WHERE m.aircraft_id = \${alias}.aircraft_id 
                  AND m.deleted_at IS NULL
                  AND md.started_at <= \${alias}.date_performed
                  AND (
                      md.started_at > (SELECT date_performed FROM last_service)
                      OR NOT EXISTS (SELECT 1 FROM last_service)
                  )
            )`,

            days_since_last_service: `(
                WITH last_service AS (
                    SELECT date_performed FROM maintenance_record 
                    WHERE aircraft_id = \${alias}.aircraft_id 
                      AND template_id = \${alias}.template_id 
                      AND date_performed < \${alias}.date_performed 
                      AND deleted_at IS NULL 
                    ORDER BY date_performed DESC LIMIT 1
                )
                SELECT GREATEST(
                    \${alias}.date_performed::date - COALESCE(
                        (SELECT date_performed::date FROM last_service),
                        (
                            SELECT MIN(md.started_at)::date 
                            FROM missions m 
                            JOIN mission_metadata md ON m.id = md.mission_id 
                            WHERE m.aircraft_id = \${alias}.aircraft_id
                              AND m.deleted_at IS NULL
                        ),
                        \${alias}.date_performed::date
                    ),
                    0
                )
            )`,
        },
        edges: {
            template: {
                resource: "maintenance_templates",
                on: "mr.template_id = mtp.id",
                joinType: "inner",
            },
            aircraft: { resource: "aircraft", on: "mr.aircraft_id = a.id", joinType: "inner" },
        },
    },
    checklists: {
        table: "checklist",
        alias: "c",
        scopes: ["org"],
        keys: ["id"],
        softDelete: "deleted_at",
        schema: {
            id: "string",
            org_id: "string",
            name: "string",
            description: "string?",
            deleted_at: "date?",
            deleted_by: "string?",
            created_at: "date",
            created_by: "string",
        },
        edges: {
            versions: { resource: "checklist_versions", on: "c.id = cv.checklist_id" },
            records: { resource: "checklist_records", on: "c.id = cr.checklist_id" },
        },
    },
    checklist_versions: {
        table: "checklist_version",
        alias: "cv",
        scopes: [],
        keys: ["checklist_id", "version"],
        schema: {
            checklist_id: "string",
            version: "number",
            form: "json",
            created_at: "date",
            created_by: "string",
        },
        computed: {
            form_length: "jsonb_array_length(${alias}.form)",
        },
        edges: {
            checklist: { resource: "checklists", on: "cv.checklist_id = c.id", joinType: "inner" },
            records: {
                resource: "checklist_records",
                on: "cv.checklist_id = cr.checklist_id AND cv.version = cr.checklist_version",
            },
        },
    },
    checklist_records: {
        table: "checklist_record",
        alias: "cr",
        scopes: ["org"],
        keys: ["id"],
        schema: {
            id: "string",
            checklist_id: "string",
            checklist_version: "number",
            org_id: "string",
            user_id: "string",
            created_at: "date",
            answers: "json",
        },
        edges: {
            version: {
                resource: "checklist_versions",
                on: "cr.checklist_id = cv.checklist_id AND cr.checklist_version = cv.version",
                joinType: "inner",
            },
            mission_links: {
                resource: "checklist_record_missions",
                on: "cr.id = crm.checklist_record_id",
            },
            match_criteria: {
                resource: "checklist_record_match_criteria",
                on: "cr.id = crmc.checklist_record_id",
            },
        },
    },
    checklist_record_match_criteria: {
        table: "checklist_record_match_criteria",
        alias: "crmc",
        scopes: [],
        keys: ["checklist_record_id"],
        schema: {
            checklist_record_id: "string",
            aircraft_id: "string?",
            pilot_in_command_id: "string?",
        },
        edges: {
            record: {
                resource: "checklist_records",
                on: "crmc.checklist_record_id = cr.id",
                joinType: "inner",
            },
            aircraft: { resource: "aircraft", on: "crmc.aircraft_id = a.id" },
            pilot: { resource: "pilots", on: "crmc.pilot_in_command_id = p.id" },
        },
    },
    checklist_record_missions: {
        table: "checklist_record_missions",
        alias: "crm",
        scopes: ["org"],
        keys: ["checklist_record_id", "mission_id"],
        schema: {
            checklist_record_id: "string",
            mission_id: "string",
            org_id: "string",
        },
        edges: {
            record: {
                resource: "checklist_records",
                on: "crm.checklist_record_id = cr.id",
                joinType: "inner",
            },
            mission: { resource: "missions", on: "crm.mission_id = m.id", joinType: "inner" },
        },
    },
    tags: {
        table: "tags",
        alias: "t",
        scopes: ["org"],
        keys: ["id"],
        schema: {
            id: "string",
            org_id: "string",
            name: "string",
        },
        computed: {
            record: "to_jsonb(${alias})",
        },
        edges: {
            mission_links: { resource: "mission_tags", on: "t.id = mt.tag_id" },
        },
    },
    mission_tags: {
        table: "mission_tags",
        alias: "mt",
        scopes: ["org"],
        keys: ["mission_id", "tag_id"],
        schema: {
            mission_id: "string",
            tag_id: "string",
            org_id: "string",
        },
        edges: {
            mission: { resource: "missions", on: "mt.mission_id = m.id", joinType: "inner" },
            tag: { resource: "tags", on: "mt.tag_id = t.id", joinType: "inner" },
        },
    },
    mission_linked_layers: {
        table: "mission_linked_layers",
        alias: "mll",
        scopes: ["org"],
        keys: ["id"],
        schema: {
            id: "string",
            mission_id: "string",
            org_id: "string",
            project_id: "string",
            layer_id: "string",
        },
        edges: {
            mission: { resource: "missions", on: "mll.mission_id = m.id" },
        },
    },
    mission_files: {
        table: "mission_files",
        alias: "mif",
        scopes: ["org"],
        keys: ["id"],
        softDelete: "deleted_at",
        schema: {
            id: "string",
            org_id: "string",
            mission_id: "string",
            name: "string",
            expires_at: "date?",
            notes: "string?",
            file_name: "string",
            created_at: "date",
            updated_at: "date?",
            deleted_at: "date?",
            deleted_by: "string?",
        },
        edges: {
            mission: { resource: "missions", on: "mif.mission_id = m.id" },
        },
    },
    mission_groups: {
        table: "mission_groups",
        alias: "mg",
        scopes: ["org"],
        keys: ["id"],
        schema: {
            id: "string",
            org_id: "string",
            name: "string",
        },
        edges: {
            missions: { resource: "missions", on: "mg.id = m.group_id" },
        },
    },
    syncs: {
        table: "syncs",
        alias: "s",
        scopes: ["org"],
        keys: ["id"],
        schema: {
            id: "string",
            org_id: "string",
            timestamp: "date",
            status: "string",
            provider: "string",
        },
        edges: {},
    },
    mission_custom_field_definitions: {
        table: "mission_custom_field_definitions",
        alias: "mcfd",
        scopes: ["org"],
        keys: ["id"],
        schema: {
            id: "string",
            org_id: "string",
            form: "json",
            created_at: "date",
            updated_at: "date?",
        },
        edges: {},
    },
    mission_custom_fields: {
        table: "mission_custom_fields",
        alias: "mcf",
        scopes: ["org"],
        keys: ["mission_id"],
        schema: {
            mission_id: "string",
            org_id: "string",
            answers: "json",
            updated_at: "date?",
        },
        edges: {
            mission: { resource: "missions", on: "mcf.mission_id = m.id", joinType: "inner" },
        },
    },
} as const
